<template>
  <div
    class="tw-z-[20] tw-rounded-md tw-border tw-border-system-warning-500 tw-bg-system-warning-50 tw-p-3"
    role="alert"
    data-test-id="msp-legacy-migration-banner"
    v-if="!isBannerClosed && visible"
  >
    <div class="tw-flex tw-items-center tw-justify-between tw-gap-2">
      <div
        class="tw-flex tw-items-center tw-justify-center tw-rounded-md tw-bg-system-warning-100 tw-p-1"
        data-test-id="msp-legacy-migration-banner-icon"
      >
        <cb-icon-attention-critical
          color="currentColor"
          class="tw-text-system-warning-500"
        />
      </div>
      <div
        class="tw-flex-grow tw-overflow-hidden tw-whitespace-normal tw-break-words"
      >
        <div
          class="tw-text-lg tw-font-semibold"
          data-test-id="msp-legacy-migration-banner-title"
        >
          {{ t("accounts-need-migration") }}
        </div>
      </div>
      <button
        class="tw-inline-flex"
        :aria-label="t('close')"
        data-test-id="msp-legacy-migration-banner-close-button"
        @click="handleClose"
      >
        <cb-icon-close-2 color="currentColor" />
      </button>
    </div>
    <div
      class="tw-ml-10 tw-text-sm"
      data-test-id="msp-legacy-migration-banner-message"
    >
      {{
        t("accounts-migration-description", {
          legacy_accounts_number: count,
          legacy_accounts_total: total,
        })
      }}
      <a
        :href="moreInfoUrl"
        target="_blank"
        rel="noopener"
        class="tw-font-semibold tw-text-primary-cobalt-blue-800 tw-underline"
        >{{ t("accounts-migration-description-here") }}</a
      >
    </div>

    <div
      class="tw-ml-10 tw-mt-3 tw-border-t tw-border-system-warning-200"
      data-test-id="msp-legacy-migration-banner-divider"
    ></div>

    <div
      class="tw-ml-10 tw-mt-3"
      data-test-id="msp-legacy-migration-banner-progress"
    >
      <div
        class="tw-flex tw-justify-end tw-text-xs tw-text-primary-grey-900"
        data-test-id="msp-legacy-migration-banner-progress-total"
      >
        {{ t("accounts-total", { legacy_accounts_total: total }) }}
      </div>
      <div
        class="tw-mt-1 tw-h-1.5 tw-w-full tw-rounded-full tw-bg-primary-grey-200"
      >
        <div
          class="tw-h-1.5 tw-rounded-full tw-bg-system-warning-500"
          data-test-id="msp-legacy-migration-banner-progress-bar"
          :style="{ width: progressPercentage + '%' }"
        ></div>
      </div>
      <div
        class="tw-mt-1 tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-primary-grey-900"
        data-test-id="msp-legacy-migration-banner-progress-updated"
      >
        <span
          class="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-system-warning-500"
        ></span>
        {{ t("accounts-migrated", { legacy_accounts_migrated: updatedCount }) }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { BANNER_COOKIE } from "@/constants/banner-cookie";
import { computed, ref } from "vue";
import { useCookies } from "@vueuse/integrations/useCookies";
import { COOKIE_SETTINGS } from "@/utils/cookies";

const { t } = useI18n();
const cookies = useCookies();

const cookieKey = ref<string>(BANNER_COOKIE.LEGACY_MIGRATION_BANNER_CLOSED);

const moreInfoUrl =
  "https://helpcenter.titanhq.com/support/solutions/articles/4000232792";

type Props = {
  visible?: boolean;
  count: number;
  total: number;
};
const props = withDefaults(defineProps<Props>(), {
  visible: false,
});

const handleClose = () => {
  // Pin path + maxAge so the dismissal holds across sibling routes and the
  // logout cleanup (which removes cookies at the same path) can clear it.
  cookies.set(cookieKey.value, true, COOKIE_SETTINGS);
};

const isBannerClosed = computed<boolean>(() => cookies.get(cookieKey.value));

const updatedCount = computed<number>(() =>
  Math.max(props.total - props.count, 0),
);

const progressPercentage = computed<number>(() => {
  if (!props.total) return 0;
  return Math.min((updatedCount.value / props.total) * 100, 100);
});
</script>

<style scoped></style>
